import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Select, Upload, Row, Col, Spin, Card, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toastError, toastSuccess } from "../../../utils/toast";
import { fetchCountries, fetchStates, fetchCities, createFirm } from "../../../api/firmApi";

export default function CreateFirmModal({ open, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Location dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const loadCountries = async () => {
    try {
      const res = await fetchCountries();
      setCountries(res || []);
    } catch {
      toastError("Failed to load countries");
    }
  };

  const loadStates = async (countryId) => {
    setLoadingStates(true);
    try {
      const res = await fetchStates(countryId);
      setStates(res || []);
      setCities([]);
      form.setFieldsValue({ state_id: null, city_id: null });
    } catch {
      toastError("Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (stateId) => {
    setLoadingCities(true);
    try {
      const res = await fetchCities(stateId);
      setCities(res || []);
      form.setFieldsValue({ city_id: null });
    } catch {
      toastError("Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCountries();
      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
    }
  }, [open]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => {
        if (dayjs.isDayjs(val)) formData.append(key, val.format("YYYY-MM-DD"));
        else formData.append(key, val);
      });

      formData.append("role", "firm");
      formData.append("status", "Active");
      if (imageFile) formData.append("image", imageFile);

      await createFirm(formData);

      toastSuccess("Firm created successfully");
      form.resetFields();
      onClose();
      onSuccess();
    } catch (error) {
      console.error(error);
      toastError("Failed to create Firm");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Modal
      title={<span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Create New Firm</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      style={{ top: 20 }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Card
          title="Basic Information"
          bordered={false}
          headStyle={{ border: 'none', padding: '0 0 16px 0' }}
          bodyStyle={{ padding: '16px 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Upload.Dragger
                  name="image"
                  beforeUpload={handleImageChange}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  style={{ width: '100%', marginBottom: 16 }}
                >
                  {imagePreview ? (
                    <Avatar
                      src={imagePreview}
                      size={120}
                      style={{ margin: '0 auto' }}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>
                      <p className="ant-upload-drag-icon">
                        <UserOutlined style={{ fontSize: '32px' }} />
                      </p>
                      <p className="ant-upload-text">Upload Profile Photo</p>
                    </div>
                  )}
                </Upload.Dragger>
              </div>
            </Col>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Firm Name"
                    name="name"
                    rules={[{ required: true, message: 'Please input firm name' }]}
                  >
                    <Input placeholder="Firm name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please input email' },
                      { type: "email", message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input placeholder="Email address" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: 'Please input password' }]}
                  >
                    <Input.Password placeholder="Password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Mobile"
                    name="mobile"
                    rules={[{ required: true, message: 'Please input mobile number' }]}
                  >
                    <Input placeholder="Mobile number" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        <Card
          title="Location Information"
          bordered={false}
          headStyle={{ border: 'none', padding: '16px 0' }}
          bodyStyle={{ padding: '16px 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Country" name="country_id" rules={[{ required: true }]}>
                <Select
                showSearch
                  placeholder="Select Country"
                  onChange={(value) => loadStates(value)}   // ✅ use state-updating helper
                  options={countries.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="State" name="state_id" rules={[{ required: true }]}>
                <Spin spinning={loadingStates}>
                  <Select
                  showSearch
                    placeholder="Select State"
                    onChange={(value) => {
                      form.setFieldsValue({ state_id: value, city_id: null }); // ✅ keep your form updates
                      loadCities(value);                                       // ✅ use city-updating helper
                    }}
                    value={form.getFieldValue("state_id")}
                    options={states.map((s) => ({
                      label: s.name,
                      value: s.id,
                    }))}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }

                  />
                </Spin>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="City"
                name="city_id"
                rules={[{ required: true, message: 'Please select a city' }]}
              >
                <Spin spinning={loadingCities}>
                  <Select
                  showSearch
                    placeholder="Select City"
                    value={form.getFieldValue("city_id")?.toString()} // ✅ keep string value handling
                    onChange={(value) => {
                      form.setFieldsValue({ city_id: value }); // ✅ keep your form updates
                    }}
                    options={cities.map((c) => ({
                      label: c.name,
                      value: c.id.toString(), // ✅ keep options as strings
                    }))}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Spin>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            style={{ marginRight: 16, minWidth: 100 }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ minWidth: 120 }}
          >
            Create
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
